import { EmptyState } from '@/components'
import {
  AddCircleOutlineIcon,
  Button,
  EditIcon,
  PlusIcon,
  Radio,
  TableColumns,
  TableColumnsColumnProps,
  Tag,
  Text,
  Tooltip,
} from '@mercai/clever'
import { ForecastsAndInventoriesTableProps } from './forecasts-and-inventories-table.type'

import { formatCurrency, formatNumber } from '@/helpers'
import { ForecastsAndInventoriesProps } from '@/types'
import { useTranslation } from 'react-i18next'
import styles from './forecasts-and-inventories-table.module.css'

export const ForecastsAndInventoriesTable = ({
  data,
  isLoading,
  onClick,
  onOpenSupplierForecastDrawer,
  onSelectedItem,
  selectedItem,
}: ForecastsAndInventoriesTableProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenBusinessV2')

  const handleShowNewSupplier = () => {
    onOpenSupplierForecastDrawer?.()
  }

  const handleShowEditSupplier = (formSupplierForecastUuid: string) => {
    onOpenSupplierForecastDrawer?.(formSupplierForecastUuid)
  }

  const columns: TableColumnsColumnProps<ForecastsAndInventoriesProps>[] = [
    {
      title: t('forecastAndInventoriesTableSupplierColumn'),
      key: 'supplier',
      render: ({ index, record }) => (
        <div className={styles['table-supplier-column']}>
          <Radio
            value={selectedItem === index}
            onChange={() => onSelectedItem?.(index)}
          />
          <span>
            <Text className={styles['table-supplier-name']}>
              {record?.inventoryTransaction?.supplier?.name ||
                record?.supplierProduct?.supplier?.name ||
                '-'}
            </Text>
            <Text
              variant="text-small"
              className={styles['table-supplier-email']}
            >
              {record?.inventoryTransaction?.supplier?.email ||
                record?.supplierProduct?.supplier?.email ||
                '-'}
            </Text>
          </span>
        </div>
      ),
      renderHeader: ({ title }) => (
        <div className={styles['table-supplier-header']}>
          <Text className={styles['table-supplier-header-text']}>
            <strong>{title}</strong>
          </Text>
          <div>
            <Button
              variant="ghost"
              size="small"
              className={styles['add-button']}
              onClick={handleShowNewSupplier}
            >
              {t('forecastAndInventoriesTableAddSupply')}
              <AddCircleOutlineIcon />
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: t('forecastAndInventoriesTableQuantityColumn'),
      key: 'quantity',
      width: '12rem',
      render: ({ record }) =>
        record?.quantity || record?.quantity === 0
          ? `${formatNumber(record?.quantity, undefined, 0)} kg`
          : '-',
    },
    {
      title: t('forecastAndInventoriesTableCostColumn'),
      key: 'priceWithoutIVA',
      width: '12rem',
      render: ({ record }) =>
        record?.cost || record?.cost === 0
          ? formatCurrency(record?.cost, 'es-MX', record?.currency || 'USD', 3)
          : '-',
    },
    {
      title: t('forecastAndInventoriesTableCategoryColumn'),
      key: 'category',
      width: '11rem',
      render: ({ record }) =>
        record?.category
          ? tEnums(`product_quality_category_${record?.category}`)
          : '-',
    },
    {
      title: t('forecastAndInventoriesTableTypeColumn'),
      key: 'type',
      width: '11rem',
      render: ({ record }) =>
        record?.inventoryTransactionUuid ? (
          <Tag variant="info" model="outline">
            {t('forecastAndInventoriesTableTypeInventoryTransactionColumn')}
          </Tag>
        ) : (
          <Tag variant="success" model="outline">
            {t('forecastAndInventoriesTableTypeForecastColumn')}
          </Tag>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: '4.5rem',
      render: ({ record }) =>
        record?.forecastUuid ? (
          <div onClick={(e) => e.stopPropagation()}>
            <Button
              variant="secondary"
              model="square"
              onClick={() => handleShowEditSupplier(record?.forecastUuid || '')}
            >
              <EditIcon />
            </Button>
          </div>
        ) : (
          <div onClick={(e) => e.stopPropagation()}>
            <Tooltip
              text={t('forecastAndInventoriesTableActionNotAllowedColumn')}
              orientation="left"
              widthText="150px"
            >
              <Button variant="secondary" model="square" disabled>
                <EditIcon />
              </Button>
            </Tooltip>
          </div>
        ),
    },
  ]

  return (
    <div className={styles['table-container']} onClick={onClick}>
      <TableColumns
        columns={columns}
        data={data || []}
        selectedRow={selectedItem}
        onSelectRow={onSelectedItem}
        isLoading={isLoading}
      />

      {!isLoading && data?.length === 0 && (
        <EmptyState
          horizontal
          title={t('supplierProductsTableEmptyTitle')}
          description={t('supplierProductsTableEmptyDescription')}
          buttonText={
            <>
              {t('supplierProductsTableEmptyButton')} <PlusIcon />
            </>
          }
          // onClickButton={handleShowNewSupplier}
        />
      )}
    </div>
  )
}
